'use client'

import { useCallback, useMemo, useState } from 'react'
import Link from 'next/link'
import { useStytchMember } from '@stytch/nextjs/b2b'
import ClientList, { ClientOption } from '@/components/client/ClientList'
import OrgProgramSwitcher from '@/components/client/OrgProgramSwitcher'
import AshLogo from '@/components/icons/client/AshLogo'
import NavMenuDesktop from './NavMenuDesktop'
import NavMenuMobile from './NavMenuMobile'
import { useRouter, usePathname } from 'next/navigation'
import { CLIENT_PAGES } from '@/components/client/constants/pages'
import type { CachedPartner } from '@/utilities/partnersCache'
import { useClientSelection } from '@/providers/ClientSelectionContext'
import { cn } from '@/utilities/ui'
import { getStytchJWT } from '@/utilities/stytch/getStytchJWT'
import { isImpersonatingSession } from '@/utilities/stytch/isImpersonatingSession'
import { AlertTriangle, House, ListChecks, type LucideIcon, UserRound } from 'lucide-react'
import SignOutIcon from '@/components/icons/SignOutIcon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { ResponsiveMode } from '@/types/responsiveMode'
import { useKitImagePrefetch } from '@/hooks/useKitImage'
import { useOrgProgramSwitcher } from '@/hooks/useOrgProgramSwitcher'
import { useKitRegistrationAccess } from '@/hooks/useKitRegistrationAccess'

type NavItem = {
  id: 'dashboard' | 'orders' | 'myProfile'
  label: string
  href: string
  Icon: LucideIcon
  platforms: readonly ResponsiveMode[]
}

const ALL_NAV_ITEMS: readonly NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    href: CLIENT_PAGES.dashboard,
    Icon: House,
    platforms: ['desktop', 'mobile'],
  },
  {
    id: 'orders',
    label: 'Orders',
    href: CLIENT_PAGES.orders,
    Icon: ListChecks,
    platforms: ['desktop', 'mobile'],
  },
  {
    id: 'myProfile',
    label: 'Profile',
    href: CLIENT_PAGES.myProfile,
    Icon: UserRound,
    platforms: ['mobile'],
  },
] as const

export const NAV_ITEMS = ALL_NAV_ITEMS.filter((item) => item.platforms.includes('desktop'))

export const MOBILE_NAV_ITEMS = ALL_NAV_ITEMS.filter((item) => item.platforms.includes('mobile'))

function mapCachedPartnersToClientOptions(partners: readonly CachedPartner[]): ClientOption[] {
  return partners.map(({ partnerId, partnerName, canPlaceOrder }: CachedPartner) => ({
    partnerId,
    partnerName,
    canPlaceOrder,
  }))
}

function SwitcherLoadingControl() {
  return (
    <output
      aria-busy="true"
      aria-label="Loading partner switcher"
      className="flex h-10 w-full items-center gap-2 rounded-full bg-cp-special-grey/10 px-4 py-2"
      data-testid="client-switcher-loading"
    >
      <span className="h-3 w-32 animate-pulse rounded bg-white/40" aria-hidden="true" />
      <span className="h-3 w-20 animate-pulse rounded bg-white/25" aria-hidden="true" />
    </output>
  )
}

export default function NavbarTop() {
  const router = useRouter()
  const pathname = usePathname()
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
  const { selectedClientId: activeClientId } = useClientSelection()
  const switcher = useOrgProgramSwitcher()
  const { onSelectFlatProgram } = switcher
  const navCapabilityClientId = switcher.capabilityPartnerId
  const clientOptions = useMemo(
    () => mapCachedPartnersToClientOptions(switcher.partners),
    [switcher.partners],
  )
  const selected = useMemo(
    () => clientOptions.find((option) => option.partnerId === activeClientId) ?? null,
    [activeClientId, clientOptions],
  )
  const canPlaceOrder = Boolean(
    switcher.partners.find((partner) => partner.partnerId === navCapabilityClientId)?.canPlaceOrder,
  )
  const { canAccessKitRegistration } = useKitRegistrationAccess(navCapabilityClientId)
  const navItems = useMemo(
    () =>
      NAV_ITEMS.map((item) =>
        item.id === 'dashboard' ? { ...item, href: switcher.dashboardHref } : item,
      ),
    [switcher.dashboardHref],
  )
  const mobileNavItems = useMemo(
    () =>
      MOBILE_NAV_ITEMS.map((item) =>
        item.id === 'dashboard' ? { ...item, href: switcher.dashboardHref } : item,
      ),
    [switcher.dashboardHref],
  )

  useKitImagePrefetch(navCapabilityClientId, canAccessKitRegistration)

  const selectOption = useCallback(
    (option: ClientOption) => {
      onSelectFlatProgram(option.partnerId, option.partnerName)
    },
    [onSelectFlatProgram],
  )

  // Keep Ash logo navigable whenever partners are available — do not block on LD/access.
  const isLogoDisabled = switcher.partnersLoading && !clientOptions.length
  const flatLoading = switcher.partnersLoading && !clientOptions.length
  const { member, isInitialized } = useStytchMember()
  const isImpersonating = Boolean(isInitialized && member) && isImpersonatingSession(getStytchJWT())
  const switcherTriggerClassName = cn(
    'h-10 rounded-full px-4 py-2',
    isImpersonating && 'text-cp-dark-blue',
  )
  const handleLogoClick = useCallback(() => {
    if (isLogoDisabled) return
    router.push(switcher.dashboardHref)
  }, [isLogoDisabled, router, switcher.dashboardHref])
  let switcherControl = <SwitcherLoadingControl />
  if (switcher.isAccessReady) {
    if (switcher.showFlatSwitcher) {
      switcherControl = (
        <ClientList
          clientOptions={clientOptions}
          selected={selected}
          loading={flatLoading}
          onSelect={selectOption}
          defaultWidth="w-full"
          triggerClassName={switcherTriggerClassName}
        />
      )
    } else {
      switcherControl = (
        <OrgProgramSwitcher
          organizations={switcher.organizations}
          selection={switcher.selection}
          loading={switcher.loading}
          onSelectOrg={switcher.onSelectOrg}
          onSelectProgram={switcher.onSelectProgram}
          triggerClassName={switcherTriggerClassName}
        />
      )
    }
  }

  return (
    <>
      <div
        data-testid="client-top-navbar-container"
        data-impersonating={isImpersonating ? 'true' : undefined}
        className={cn(
          'w-full flex flex-col gap-4 py-4',
          'px-4 sm:flex-row sm:h-16 sm:items-center sm:justify-between sm:gap-4 sm:px-9 sm:py-0 sm:min-w-0',
          isImpersonating && 'bg-warning-fill',
        )}
      >
        <div
          className="flex w-full min-w-0 items-center gap-4 sm:flex-1 sm:w-0"
          data-testid="client-top-menu-left"
        >
          <div className="shrink-0">
            <AshLogo
              id="ashLogo"
              color={isImpersonating ? 'var(--brand-dark-blue)' : 'white'}
              className={cn(
                'h-8 w-auto',
                isImpersonating ? 'text-cp-dark-blue' : 'text-white',
                isLogoDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
              onClick={handleLogoClick}
            />
          </div>
          {isImpersonating ? (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span data-testid="impersonating-indicator" className="inline-flex shrink-0">
                    <AlertTriangle
                      className="h-5 w-5 text-cp-dark-blue"
                      aria-label="Impersonating"
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent>Impersonating</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
          <div className="h-10 min-w-0 flex-1 sm:max-w-88">{switcherControl}</div>
          <Link
            href="/logout?reason=logout"
            aria-label="Log out"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cp-special-grey/10 transition-colors hover:bg-cp-special-grey/20 sm:hidden"
            data-testid="client-top-mobile-logout"
          >
            <SignOutIcon
              className="h-5 w-5 text-cp-coral"
              aria-hidden="true"
              data-testid="client-top-mobile-logout-icon"
            />
          </Link>
        </div>
        <div className="hidden shrink-0 sm:block" data-testid="desktop-nav-container">
          <NavMenuDesktop
            items={navItems}
            pathname={pathname}
            canPlaceOrder={canPlaceOrder}
            canAccessKitRegistration={canAccessKitRegistration}
            isProfileMenuOpen={isProfileMenuOpen}
            onProfileMenuOpenChange={setIsProfileMenuOpen}
            isProgramScopedNavDisabled={switcher.isProgramScopedNavDisabled}
            isImpersonating={isImpersonating}
          />
        </div>
      </div>
      <div className="sm:hidden" data-testid="mobile-nav-container">
        <NavMenuMobile
          items={mobileNavItems}
          pathname={pathname}
          canAccessKitRegistration={canAccessKitRegistration}
          isProgramScopedNavDisabled={switcher.isProgramScopedNavDisabled}
        />
      </div>
    </>
  )
}
